import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const PromoStoreCategoriesDialog = ({
  open,
  categories = [],
  products = [],
  saving = false,
  onClose,
  onCreate,
  onRename,
  onDelete,
  onMove,
}) => {
  const [draftName, setDraftName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const countsByCategoryId = useMemo(() => {
    const counts = new Map();
    (Array.isArray(products) ? products : []).forEach((product) => {
      const categoryId =
        product?.categoryId ??
        product?.CategoryId ??
        product?.category?.id ??
        product?.Category?.id ??
        null;
      if (!categoryId) return;
      counts.set(String(categoryId), (counts.get(String(categoryId)) || 0) + 1);
    });
    return counts;
  }, [products]);

  const resetCreate = () => setDraftName("");
  const stopEditing = () => {
    setEditingCategoryId(null);
    setEditingName("");
  };

  const handleCreate = async () => {
    const name = draftName.trim();
    if (!name) return;
    const created = await onCreate?.(name);
    if (created !== false) resetCreate();
  };

  const handleRename = async () => {
    const name = editingName.trim();
    if (!editingCategoryId || !name) return;
    const renamed = await onRename?.(editingCategoryId, name);
    if (renamed !== false) stopEditing();
  };

  const beginEditing = (category) => {
    setEditingCategoryId(category.id);
    setEditingName(category.name || "");
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Categories</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "text.secondary", mb: 2 }}>
          Organize products in this PromoStore.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="New Category"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            disabled={saving}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleCreate();
              }
            }}
          />
          <Button variant="contained" onClick={handleCreate} disabled={saving || !draftName.trim()}>
            Create
          </Button>
        </Stack>

        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {categories.map((category, index) => {
            const count = countsByCategoryId.get(String(category.id)) || 0;
            const isEditing = String(editingCategoryId) === String(category.id);
            return (
              <ListItem
                key={category.id}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Category Name"
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      disabled={saving}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleRename();
                        }
                      }}
                    />
                  ) : (
                    <ListItemText
                      primary={category.name}
                      secondary={`${count} product${count === 1 ? "" : "s"}`}
                    />
                  )}
                </Box>

                <Stack direction="row" spacing={0.25} alignItems="center">
                  <IconButton
                    size="small"
                    disabled={saving || index === 0}
                    onClick={() => onMove?.(category.id, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={saving || index === categories.length - 1}
                    onClick={() => onMove?.(category.id, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  {isEditing ? (
                    <>
                      <Button size="small" onClick={stopEditing} disabled={saving}>
                        Cancel
                      </Button>
                      <Button size="small" variant="contained" onClick={handleRename} disabled={saving || !editingName.trim()}>
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" disabled={saving} onClick={() => beginEditing(category)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={saving} onClick={() => onDelete?.(category)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </ListItem>
            );
          })}
          {!categories.length && (
            <Box
              sx={{
                border: (theme) => `1px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                px: 2,
                py: 3,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              No categories yet.
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoStoreCategoriesDialog;
